/**
 * Verify before you can message.
 *
 * Built 11 Sep 2026. Onboarding was cut from sixteen steps to nine that morning
 * and the government ID and confirmation video left the path with it. That made
 * signup fast and quietly made UN8 an unverified network, which is most of what
 * this product sells. This gate puts the trust back without putting the wall
 * back in front of signup.
 *
 * The rule: anyone may browse, be dealt a weekly four, and heart a profile.
 * Nobody may SEND A MESSAGE until their ID and confirmation video are in.
 *
 * Submitted is enough to message - not approved. Human review takes days and a
 * person who has just handed over their passport should not sit in silence
 * waiting for it. The moderator still holds rejection, which bans afterwards.
 *
 * Hearts are deliberately NOT gated. Hearting is how a match is made in the
 * first place; gate that and a new account sees a dead app and leaves.
 *
 * Enforcement lives in matchStore.sendMessage, not in the composer - the store
 * is the enforcement point, same convention as commentStore.
 */

import { onboardingStore } from "../auth/onboardingStore";
import { mediaIsDurable } from "../media/uploadService";
import type { MediaAsset } from "../domain/types";

export type VerifyState = "unverified" | "submitted" | "verified";

export interface VerifyStatus {
  state: VerifyState;
  /** True when the asset has never been durably uploaded. */
  needsId: boolean;
  needsVideo: boolean;
}

/** Durable upload = it survived a reload and has a real storage key. */
function submitted(asset: MediaAsset | null): boolean {
  return Boolean(asset) && mediaIsDurable(asset);
}

function approved(asset: MediaAsset | null): boolean {
  return asset?.reviewStatus === "approved";
}

export function readVerifyStatus(userId: string | null): VerifyStatus {
  if (!userId) {
    return { state: "unverified", needsId: true, needsVideo: true };
  }
  const row = onboardingStore.load(userId);
  const id = row?.answers.governmentId ?? null;
  const video = row?.answers.confirmationVideo ?? null;

  if (approved(id) && approved(video)) {
    return { state: "verified", needsId: false, needsVideo: false };
  }
  const needsId = !submitted(id);
  const needsVideo = !submitted(video);
  if (!needsId && !needsVideo) {
    return { state: "submitted", needsId: false, needsVideo: false };
  }
  return { state: "unverified", needsId, needsVideo };
}

/**
 * Machine-readable marker so the UI can tell "you must verify" apart from
 * "you used your weekly message" and open the verification flow instead of
 * just printing red text. Match on this, never on the sentence.
 */
export const VERIFY_REQUIRED = "verify_required";

export interface GateOk {
  ok: true;
}
export interface GateBlocked {
  ok: false;
  error: string;
  reason: typeof VERIFY_REQUIRED;
  needsId: boolean;
  needsVideo: boolean;
}

/** The gate itself. Called by matchStore.sendMessage before anything else. */
export function canMessage(userId: string | null): GateOk | GateBlocked {
  const s = readVerifyStatus(userId);
  if (s.state !== "unverified") return { ok: true };

  const missing =
    s.needsId && s.needsVideo
      ? "your ID and a short confirmation video"
      : s.needsId
        ? "your ID"
        : "a short confirmation video";

  return {
    ok: false,
    error: `Everyone here is a verified person. Add ${missing} to start a conversation.`,
    reason: VERIFY_REQUIRED,
    needsId: s.needsId,
    needsVideo: s.needsVideo,
  };
}

const VERIFY_RETURN_KEY = "un8.verify.return.v1";

/** Hash pane for MatchApp. `null` means this is not the verify door. */
export function verifyPaneFromHash(
  hash = typeof window === "undefined" ? "" : window.location.hash
): "id" | "video" | null {
  const path = hash.replace(/^#\/?/, "").split("?")[0];
  if (path === "match/verify/video") return "video";
  if (path === "match/verify/id" || path === "match/verify") return "id";
  if (path.startsWith("match/verify/")) return "id";
  return null;
}

export function openVerify(opts: {
  needsId: boolean;
  needsVideo: boolean;
  returnHash?: string;
}): void {
  try {
    sessionStorage.setItem(
      VERIFY_RETURN_KEY,
      opts.returnHash ?? window.location.hash ?? "#/match/profile"
    );
  } catch {
    /* ignore */
  }
  const pane = opts.needsId ? "id" : "video";
  window.location.hash = `#/match/verify/${pane}`;
}

export function closeVerify(): void {
  let back = "#/match/profile";
  try {
    back = sessionStorage.getItem(VERIFY_RETURN_KEY) || back;
    sessionStorage.removeItem(VERIFY_RETURN_KEY);
  } catch {
    /* ignore */
  }
  window.location.hash = back;
}
